import React, { useCallback, useState, useEffect, useRef } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import { useSelector } from "react-redux";
import useThemeColor from "../../../hooks/useThemeColor";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import ToggleSwitch from "../Switchery/switchery";
import ModifierGroupSelectionModal from "./modifierGroupSelectionModal";

const ModifierGroupsSelector = ({
  isEnabled,
  setIsEnabled,
  selectedGroups,
  setSelectedGroups,
  showModal,
  setShowModal,
  onModalSubmit,
  setLoading,
}) => {
  const [modifierGroups, setModifierGroups] = useState([]);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const isDarkMode = useDarkMode();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const CompID = useSelector((state) => state.auth.user).userData?.compID;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  
  // Drag and drop refs
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

 // In ModifierGroupsSelector component
const removeModifierGroup = (groupId) => {
  // Create a completely new array to avoid reference issues
  const stringGroupId = String(groupId);
  const updatedGroups = selectedGroups
    .filter(g => String(g.modifierGroupID) !== stringGroupId);
  
  
  // Call parent's setter with new array
  setSelectedGroups(updatedGroups);
};

// Drag and drop handlers
const handleDragStart = (e, index) => {
  dragItem.current = index;
  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData("application/json", JSON.stringify(index));
  e.currentTarget.style.opacity = "0.5";
  e.currentTarget.style.cursor = "grabbing";
};

const handleDragEnter = (e, index) => {
  e.preventDefault();
  dragOverItem.current = index;
  e.currentTarget.style.backgroundColor = "#e9ecef";
};

const handleDragLeave = (e) => {
  e.preventDefault();
  e.currentTarget.style.backgroundColor = "";
};

const handleDragOver = (e) => {
  e.preventDefault();
};

const handleDrop = (e) => {
  e.preventDefault();
  const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
  const overIndex = dragOverItem.current;
  
  if (
    draggedIndex === overIndex ||
    draggedIndex === undefined ||
    overIndex === undefined
  ) {
    dragItem.current = null;
    dragOverItem.current = null;
    return;
  }
  
  const newGroups = [...selectedGroups];
  const draggedGroup = newGroups[draggedIndex];
  newGroups.splice(draggedIndex, 1);
  newGroups.splice(overIndex, 0, draggedGroup);
  
  setSelectedGroups(newGroups);
  dragItem.current = null;
  dragOverItem.current = null;
};

const handleDragEnd = (e) => {
  e.currentTarget.style.opacity = "1";
  e.currentTarget.style.cursor = "grab";
  document.querySelectorAll(".modifier-group-chip").forEach((chip) => {
    chip.style.backgroundColor = "";
  });
  dragItem.current = null;
  dragOverItem.current = null;
};

  const fetchModifierGroups = useCallback(async () => {
    if (hasLoadedData || !CompID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_MODIFIER_GROUPS, {
        params: {
          CompID,
          SortBy: "Name",
          SortOrder: "ASC",
          Search: "",
        },
      });

      const modifierData = response.data.data || response.data;
      if (Array.isArray(modifierData)) {
        setModifierGroups(modifierData);
        setHasLoadedData(true);
      } else {

        setModifierGroups([]);
      }
    } catch (err) {

      setModifierGroups([]);
    } finally {
      setLoading(false);
    }
  }, [CompID, hasLoadedData, setLoading]);

  useEffect(() => {
    if (showModal && !hasLoadedData) {
      fetchModifierGroups();
    }
  }, [showModal, hasLoadedData, fetchModifierGroups]);

  

  // Safeguard: Ensure selectedGroups is an array
  const safeSelectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];

  return (
    <div 
      className="card p-3"
      style={{
        backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
        border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
        color: isDarkMode ? '#ffffff' : '#000000',
      }}
    >
      {/* Toggle switch removed - content always visible */}
      <div className="mt-3">
          {safeSelectedGroups.length > 0 ? (
            <>
              <div className="row row-cols-1 row-cols-md-2 g-3">
                {safeSelectedGroups.map((group, index) => (
                  <div key={group.modifierGroupID} className="col">
                    <div 
                      className="modifier-group-chip"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      style={{
                        background: isDarkMode ? '#404040' : '#ffffff',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
                        transition: 'all 0.2s ease',
                        cursor: 'grab',
                        position: 'relative',
                        boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = themeColor;
                        e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}25`;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
                        e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {/* Drag Handle */}
                          <div 
                            style={{
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '8px',
                              cursor: 'grab',
                              color: isDarkMode ? '#ffffff' : '#6c757d',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = isDarkMode ? '#555' : '#f8f9fa';
                              e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#495057';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#6c757d';
                            }}
                          >
                            <i className="fa fa-bars" style={{ fontSize: '12px' }}></i>
                          </div>
                          
                          {/* Add-on Group Icon */}
                          <div 
                            style={{
                              width: '36px',
                              height: '36px',
                              background: isDarkMode ? '#555' : '#f8f9fa',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '12px',
                              border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                            }}
                          >
                            <i className="fa fa-cogs" style={{ fontSize: '14px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                          </div>
                          
                          {/* Add-on Group Name */}
                          <div>
                            <span 
                              style={{ 
                                fontSize: '15px', 
                                fontWeight: '500',
                                color: isDarkMode ? '#ffffff' : '#495057'
                              }}
                            >
                              {group.name}
                            </span>
                          </div>
                        </div>
                        
                        {/* Remove Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeModifierGroup(group.modifierGroupID);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            color: '#dc3545'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = isDarkMode ? '#4a1a1a' : '#f8d7da';
                            e.currentTarget.style.color = isDarkMode ? '#ff6b6b' : '#721c24';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = '#dc3545';
                          }}
                        >
                          <i 
                            className="fa fa-times" 
                            style={{ 
                              fontSize: '12px'
                            }}
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-center mt-4">
                <button
                  onClick={() => {
                    if (!hasLoadedData) fetchModifierGroups();
                    setShowModal(true);
                  }}
                  style={{
                    background: themeColor,
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = themeColor + 'dd';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = themeColor;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                  Choose More Add-on Groups
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <div 
                style={{
                  width: '60px',
                  height: '60px',
                  background: isDarkMode ? '#555' : '#f8f9fa',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                }}
              >
                <i className="fa fa-cogs" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
              </div>
              <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', marginBottom: '20px' }}>No Add-on groups selected</p>
              <button
                onClick={() => {
                  if (!hasLoadedData) fetchModifierGroups();
                  setShowModal(true);
                }}
                style={{
                  background: themeColor,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 auto'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = themeColor + 'dd';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = themeColor;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                Choose Add-on Group
              </button>
            </div>
          )}
        </div>

      <ModifierGroupSelectionModal
        showModal={showModal}
        setShowModal={setShowModal}
        showButton={true}
        
        onSubmit={onModalSubmit}
        setSelectedModifierGroups={setSelectedGroups}
        selectedModifierGroups={safeSelectedGroups}
        modifierGroups={modifierGroups}
        isMultiSelect={true}
        modalTitle="Select Add-on Groups"
        modalSubTitle="Select the add-on groups for the item"
      />
    </div>
  );
};

export default ModifierGroupsSelector;